import PropTypes from 'prop-types';
import {
  Stack,
  Typography,
  Box,
  Chip,
  MenuItem,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import Iconify from 'src/components/iconify';
import { RHFCheckbox, RHFSelect, RHFTextField } from 'src/components/hook-form';
import { useJobCreationContext } from 'src/sections/job2/hooks/use-job-creation-context';

const QUESTION_TYPES = [
  {
    label: 'Notice period',
    value: 'noticePeriod',
    question:
      'What is the minimum notice period you require before joining (in days)?',
    type: 'TEXT',
    isQualifyingQuestion: false,
  },
  {
    label: 'Current City',
    value: 'currentCity',
    question: 'Which city are you currently in?',
    type: 'TEXT',
    isQualifyingQuestion: false,
  },
  {
    label: 'Visa sponsorship',
    value: 'visaSponsorship',
    question: 'Would you require visa sponsorship?',
    type: 'BOOLEAN',
    isQualifyingQuestion: true,
  },
  {
    label: 'On-site work',
    value: 'onSiteWork',
    question: 'Are you comfortable working in an onsite setting?',
    type: 'BOOLEAN',
    isQualifyingQuestion: true,
  },
  {
    label: 'Remote Work',
    value: 'remoteWork',
    question: 'Are you comfortable working in a remote setting?',
    type: 'BOOLEAN',
    isQualifyingQuestion: true,
  },
  {
    label: 'Why us',
    value: 'whyUs',
    question: 'Why do you want to join us?',
    type: 'TEXT',
    isQualifyingQuestion: false,
  },
  {
    label: 'Current salary',
    value: 'currentSalary',
    question: 'What is your current salary (Per Annum)?',
    type: 'NUMERIC',
    isQualifyingQuestion: true,
  },
  {
    label: 'Expected salary',
    value: 'expectedSalary',
    question: 'What is your expected salary (Per Annum)?',
    type: 'NUMERIC',
    isQualifyingQuestion: true,
  },
  {
    label: 'Add Question',
    value: 'custom',
    question: '',
    type: 'TEXT',
    isQualifyingQuestion: false,
  },
];

export default function PreScreening({
  errors,
  watch,
  setValue,
  isPreScreeningExpanded,
  setIsPreScreeningExpanded,
  preScreeningQuestionFields,
  appendPreScreeningQuestion,
  removePreScreeningQuestion,
}) {
  // hooks
  const { canEdit, mode, clickedStage, setClickedStage } =
    useJobCreationContext();

  // ref
  const accordianRef = useRef(null);

  // states
  const [isHoveredPreScreeningQuestion, setIsHoveredPreScreeningQuestion] =
    useState('');

  return (
    <Accordion
      ref={accordianRef}
      onChange={() => {
        if (clickedStage === 'PRESCREENING') {
          setClickedStage('SCREENING');
        }
      }}
      sx={{
        width: '100%',
      }}
      expanded={isPreScreeningExpanded}
    >
      <AccordionSummary>
        <Stack spacing="24px" sx={{ width: '100%' }}>
          <Stack spacing="12px">
            <Typography variant="h3">Pre-Screening</Typography>

            <Typography
              variant="input"
              color={theme => theme.palette.content.primaryBlack3}
            >
              Find out basic information about your candidates: E.g., What is
              the minimum notice period you require before joining?
            </Typography>
          </Stack>
          {!isPreScreeningExpanded && (
            <Button
              variant={
                mode !== 'EDIT' && clickedStage === 'PRESCREENING'
                  ? 'contained'
                  : 'soft'
              }
              fullWidth
              onClick={() => {
                setIsPreScreeningExpanded(!isPreScreeningExpanded);
              }}
              endIcon={<Iconify icon="tabler:chevron-down" />}
            >
              Custom Setup
            </Button>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          <Stack spacing={4}>
            {preScreeningQuestionFields.map((item, index) => {
              const parentField = `preScreeningQuestions.${index}`;
              const questionFieldName = `${parentField}.question`;
              const responseTypeFieldName = `${parentField}.responseType`;
              const qualifyFieldName = `${parentField}.isQualifyingQuestion`;

              const minFieldName = `${parentField}.min`;
              const maxFieldName = `${parentField}.max`;

              const booleanAnswerFieldName = `${parentField}.booleanAnswer`;

              const responseType = watch(`${parentField}.responseType`);
              const title = watch(`${parentField}.title`);

              const getTitle = () => {
                if (title === 'Question') {
                  const customQuestionsLength =
                    preScreeningQuestionFields.filter(_item =>
                      _item.title.includes('Question'),
                    ).length;
                  setValue(
                    `${parentField}.title`,
                    `Question ${customQuestionsLength}`,
                  );
                }
                return title;
              };

              return (
                <Stack
                  key={item.id}
                  spacing={1}
                  sx={{
                    mt: index === 0 && '24px',
                    position: 'relative',
                  }}
                  onMouseEnter={() => setIsHoveredPreScreeningQuestion(index)}
                  onMouseLeave={() => setIsHoveredPreScreeningQuestion('')}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      width: '100%',
                    }}
                  >
                    <Typography variant="subheading2">{getTitle()}</Typography>

                    {canEdit && (
                      <Box
                        sx={{
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          padding: '2px',
                          border: theme =>
                            `0.5px solid ${theme.palette.content.primaryBlack3}`,
                          alignSelf: 'flex-end',
                          visibility:
                            isHoveredPreScreeningQuestion === index
                              ? 'visible'
                              : 'hidden',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                        }}
                        disabled={!canEdit}
                        onClick={() => {
                          if (canEdit) removePreScreeningQuestion(index);
                        }}
                      >
                        <Iconify icon="tabler:trash" />
                      </Box>
                    )}
                  </Stack>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                      <Stack spacing={1} sx={{ width: '80%' }}>
                        <Typography variant="input" fontWeight={500}>
                          Question
                        </Typography>
                        <RHFTextField
                          fullWidth
                          name={questionFieldName}
                          disabled={!canEdit}
                          placeholder="Question text"
                        />
                      </Stack>
                      <Stack spacing={1} sx={{ width: '20%' }}>
                        <Typography variant="input" fontWeight={500}>
                          Answer type
                        </Typography>
                        <RHFSelect
                          name={responseTypeFieldName}
                          labelId="Response Type"
                          sx={{
                            width: '100%',
                          }}
                          disabled={!canEdit}
                          onChange={e => {
                            const type = e.target.value;
                            const isQualifying =
                              type === 'BOOLEAN' || type === 'NUMERIC';
                            setValue(qualifyFieldName, isQualifying);
                          }}
                        >
                          <MenuItem key="NUMERIC" value="NUMERIC">
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing="6px"
                            >
                              <Iconify icon="tabler:hash" />
                              <Typography variant="caption">Numeric</Typography>
                            </Stack>
                          </MenuItem>
                          <MenuItem key="BOOLEAN" value="BOOLEAN">
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing="6px"
                            >
                              <Iconify icon="tabler:circle-half-2" />
                              <Typography variant="caption">Yes/No</Typography>
                            </Stack>
                          </MenuItem>
                          <MenuItem key="TEXT" value="TEXT">
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing="6px"
                            >
                              <Iconify icon="tabler:align-left" />
                              <Typography variant="caption">Text</Typography>
                            </Stack>
                          </MenuItem>
                        </RHFSelect>
                      </Stack>
                    </Stack>
                    <Stack>
                      {responseType !== 'TEXT' && (
                        <RHFCheckbox
                          sx={{
                            fontWeight: 400,
                            '& .MuiTypography-root': {
                              fontSize: '12px',
                            },
                          }}
                          name={qualifyFieldName}
                          label="Must Qualify"
                          disabled={!canEdit}
                        />
                      )}
                    </Stack>
                  </Stack>
                  <Stack mt="16px">
                    {(responseType === 'BOOLEAN' ||
                      responseType === 'NUMERIC') && (
                      <Typography variant="input" fontWeight={500}>
                        Ideal response
                      </Typography>
                    )}
                    {responseType === 'BOOLEAN' && (
                      <RHFSelect
                        sx={{ width: 'min-content', mt: '16px' }}
                        name={booleanAnswerFieldName}
                        disabled={!canEdit}
                      >
                        <MenuItem key="YES" value="YES">
                          Yes
                        </MenuItem>
                        <MenuItem key="NO" value="NO">
                          No
                        </MenuItem>
                      </RHFSelect>
                    )}
                    {responseType === 'NUMERIC' && (
                      <Stack
                        direction="row"
                        spacing="10px"
                        mt="16px"
                        width="80%"
                      >
                        <RHFTextField
                          name={minFieldName}
                          placeholder="Min"
                          disabled={!canEdit}
                          isNumeric
                        />
                        <Typography fontWeight={500} mt="5px">
                          -
                        </Typography>
                        <RHFTextField
                          name={maxFieldName}
                          placeholder="Max"
                          disabled={!canEdit}
                          isNumeric
                        />
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
          <Stack spacing={2} py={1}>
            <Typography variant="h4">Add question</Typography>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              flexWrap="wrap"
            >
              {QUESTION_TYPES.filter(questionType => {
                if (questionType.value === 'custom') {
                  return true;
                }
                return !preScreeningQuestionFields.some(
                  field => field.title === questionType.label,
                );
              }).map(questionType => (
                <Chip
                  key={questionType.value}
                  icon={
                    questionType.value === 'custom' && (
                      <Iconify icon="tabler:plus" />
                    )
                  }
                  color="info"
                  variant="soft"
                  label={questionType.label}
                  disabled={!canEdit}
                  onClick={() =>
                    appendPreScreeningQuestion({
                      title:
                        questionType.value === 'custom'
                          ? 'Question'
                          : questionType.label,
                      question: questionType.question,
                      responseType: questionType.type,
                      booleanAnswer: 'YES',
                      isQualifyingQuestion: questionType.isQualifyingQuestion,
                    })
                  }
                  sx={{
                    bgcolor:
                      questionType.value === 'custom' &&
                      'background.primaryWhite1',
                    border: theme =>
                      questionType.value === 'custom' &&
                      `1px solid ${theme.palette.border.primaryAccent1}`,
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Stack>
      </AccordionDetails>
      <AccordionActions>
        <Stack alignItems="center" sx={{ width: '100%' }} spacing={3}>
          {isPreScreeningExpanded && (
            <Button
              fullWidth
              variant="soft"
              onClick={() => setIsPreScreeningExpanded(!isPreScreeningExpanded)}
              endIcon={<Iconify icon="tabler:chevron-up" />}
            >
              View less
            </Button>
          )}
        </Stack>
      </AccordionActions>
    </Accordion>
  );
}

PreScreening.propTypes = {
  errors: PropTypes.object,
  watch: PropTypes.func,
  setValue: PropTypes.func,
  isPreScreeningExpanded: PropTypes.bool,
  setIsPreScreeningExpanded: PropTypes.func,
  preScreeningQuestionFields: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string,
      responseType: PropTypes.string,
      isQualifyingQuestion: PropTypes.bool,
    }),
  ),
  appendPreScreeningQuestion: PropTypes.func,
  removePreScreeningQuestion: PropTypes.func,
};
